/**
 * @zendir/ui - TelemetryStreamCard Component
 *
 * Compact streaming telemetry card showing individual metric channels.
 * Designed for real-time data display with configurable metrics.
 *
 * Features:
 * - Configurable metric channels
 * - Streaming status indicator
 * - Color-coded values based on thresholds
 * - WCAG 2.1 AA accessibility
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../theme";
import { Icon } from "../core/display/Icon";
import { CardHeader } from "../core/display/CardHeader";
import { useCompactMode } from "../hooks";
import { classNames, type StatusLevel } from "../utils";

export interface TelemetryMetric {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Current value */
  value: number | string;
  /** Unit suffix */
  unit?: string;
  /** Status level for color coding */
  status?: StatusLevel;
  /** Format function for value display */
  format?: (value: number) => string;
}

export interface TelemetryStreamCardProps {
  /** Card title */
  title?: string;
  /** Number of channels */
  channelCount?: number;
  /** Update frequency (Hz) */
  frequency?: number;
  /** Streaming status */
  isStreaming?: boolean;
  /** Metric channels to display */
  metrics?: TelemetryMetric[];
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
  /** Enable compact mode - shows condensed view that expands on hover/click */
  compact?: boolean;
  /** Start expanded when in compact mode (pinned state) */
  defaultExpanded?: boolean;
  /** Callback when pin state changes in compact mode */
  onPinChange?: (isPinned: boolean) => void;
}

/**
 * Get status color for a metric value
 */
function getMetricColor(status: StatusLevel | undefined, tokens: any): string {
  if (!status || status === "normal") return tokens.colors.status.normal;
  if (status === "caution") return tokens.colors.status.caution;
  if (status === "critical") return tokens.colors.status.critical;
  if (status === "standby") return tokens.colors.status.standby;
  return tokens.colors.text.primary;
}

/**
 * TelemetryStreamCard - Compact streaming telemetry display
 *
 * @example
 * ```tsx
 * <TelemetryStreamCard
 *   title="Telemetry"
 *   channelCount={12}
 *   frequency={1}
 *   isStreaming={true}
 *   metrics={[
 *     { id: 'cpu', label: 'CPU Temp', value: 42, unit: '°C', status: 'normal' },
 *     { id: 'voltage', label: 'Bus Voltage', value: 28.4, unit: 'V' },
 *     { id: 'wheel', label: 'Wheel Speed', value: 2450, unit: 'rpm' },
 *     { id: 'memory', label: 'Memory', value: 78, unit: '%', status: 'caution' },
 *   ]}
 * />
 * ```
 */
export const TelemetryStreamCard = memo(function TelemetryStreamCard({
  title = "Telemetry",
  channelCount = 12,
  frequency = 1,
  isStreaming = true,
  metrics = [],
  loading = false,
  className = "",
  compact = false,
  defaultExpanded = false,
  onPinChange,
}: TelemetryStreamCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";
  const useGlassMode = isBoldVariant || isMinimalVariant;

  // Glass accent colors for bold/minimal themes (matching Container/AstroChart)
  const glassAccentColor = tokens.colors.accent.primary;
  const glassAccentMuted = `${glassAccentColor}66`;

  // Compact mode state management
  const { expanded, isPinned, handlers, transitionDuration } = useCompactMode({
    compact,
    defaultExpanded,
    onPinChange,
  });

  // Glass mode container style (matching Container/AstroChart for bold/minimal)
  const glassContainerStyle: React.CSSProperties = useGlassMode
    ? {
        backgroundColor: "rgba(10, 15, 25, 0.35)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${glassAccentMuted}`,
        borderTop: isBoldVariant
          ? `2px solid ${glassAccentColor}`
          : `1px solid ${glassAccentColor}`,
      }
    : {};

  // Card styles - Use transparent background with blur for transparent themes (matching Timeline)
  const cardStyle = useMemo(
    () => ({
      position: "relative" as const,
      overflow: "hidden" as const,
      ...(useGlassMode
        ? glassContainerStyle
        : {
            backgroundColor: isTransparentTheme
              ? "transparent"
              : tokens.colors.background.surface,
            ...(isTransparentTheme && {
              backdropFilter: "blur(12px)" as const,
              WebkitBackdropFilter: "blur(12px)" as const,
            }),
            ...(tokens.colors.border.cardStyleDefault ?? {
              border: `1px solid ${tokens.colors.border.default}`,
            }),
          }),
      borderRadius: tokens.borderRadius.lg,
      padding: tokens.spacing.md,
    }),
    [tokens, isTransparentTheme, useGlassMode, glassContainerStyle],
  );

  // Metric box styles
  const metricBoxStyle = useMemo(
    () => ({
      backgroundColor: tokens.colors.background.elevated,
      borderRadius: tokens.borderRadius.md,
      padding: tokens.spacing.sm,
    }),
    [tokens],
  );

  // Loading state
  if (loading) {
    return (
      <article
        className={classNames("zendir-telemetry-stream-card", className)}
        style={cardStyle}
        aria-busy="true"
        aria-label="Loading telemetry stream"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div>
            <div
              style={{
                height: 16,
                width: 80,
                backgroundColor: tokens.colors.background.elevated,
                borderRadius: 4,
                marginBottom: 4,
              }}
            />
            <div
              style={{
                height: 12,
                width: 100,
                backgroundColor: tokens.colors.background.elevated,
                borderRadius: 4,
              }}
            />
          </div>
          <div
            style={{
              height: 24,
              width: 70,
              backgroundColor: tokens.colors.background.elevated,
              borderRadius: 12,
            }}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 12,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={metricBoxStyle}>
              <div
                style={{
                  height: 10,
                  width: 60,
                  backgroundColor: tokens.colors.background.base,
                  borderRadius: 4,
                  marginBottom: 8,
                }}
              />
              <div
                style={{
                  height: 20,
                  width: 80,
                  backgroundColor: tokens.colors.background.base,
                  borderRadius: 4,
                }}
              />
            </div>
          ))}
        </div>
      </article>
    );
  }

  // Compact view - shows streaming status and first few metrics
  if (compact && !expanded) {
    return (
      <article
        className={classNames(
          "zendir-telemetry-stream-card",
          "compact",
          className,
        )}
        {...handlers}
        style={{
          ...cardStyle,
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          cursor: "pointer",
          transition: `all ${transitionDuration} ease-out`,
        }}
        onMouseEnter={(e) => {
          handlers.onMouseEnter();
          if (!prefersReducedMotion) {
            (e.currentTarget as HTMLElement).style.transform = "scale(1.01)";
            (e.currentTarget as HTMLElement).style.boxShadow =
              `0 4px 12px ${tokens.colors.accent.primary}20`;
          }
        }}
        onMouseLeave={(e) => {
          handlers.onMouseLeave();
          (e.currentTarget as HTMLElement).style.transform = "scale(1)";
          (e.currentTarget as HTMLElement).style.boxShadow = "";
        }}
        aria-label={`${title} (compact)`}
      >
        {/* Glass gradient overlay for bold/minimal themes */}
        {useGlassMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.spacing.md,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Left side: Status indicator + Title + Accent line */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: isStreaming
                  ? tokens.colors.status.normal
                  : tokens.colors.status.off,
                boxShadow: isStreaming
                  ? `0 0 6px ${tokens.colors.status.normal}`
                  : "none",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontWeight: tokens.typography.fontWeight.medium,
                color: useGlassMode ? glassAccentColor : undefined,
                textShadow: useGlassMode
                  ? `0 0 16px ${glassAccentMuted}`
                  : undefined,
              }}
            >
              {title.split(" ")[0]}
            </span>
            {useGlassMode && (
              <div
                style={{
                  flex: 1,
                  height: isBoldVariant ? "2px" : "1px",
                  minWidth: 20,
                  background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 60%, transparent 100%)`,
                  boxShadow: isBoldVariant
                    ? `0 0 8px ${glassAccentMuted}`
                    : undefined,
                }}
              />
            )}
          </div>

          {/* Right side: Channel count badge + Metrics + Pin */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                color: tokens.colors.text.tertiary,
                fontSize: tokens.typography.fontSize.xs,
                backgroundColor: useGlassMode
                  ? `${glassAccentColor}20`
                  : undefined,
                padding: useGlassMode ? "1px 6px" : undefined,
                borderRadius: tokens.borderRadius.sm,
              }}
            >
              {channelCount}ch
            </span>
            {metrics.slice(0, 2).map((metric) => (
              <span
                key={metric.id}
                style={{
                  fontVariantNumeric: "tabular-nums",
                  fontFamily: tokens.typography.fontFamily.mono,
                  fontSize: tokens.typography.fontSize.xs,
                  color: getMetricColor(metric.status, tokens),
                }}
              >
                {metric.value}
                {metric.unit ? metric.unit : ""}
              </span>
            ))}
            {isPinned && (
              <Icon
                name="lock"
                size={12}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className={classNames(
        "zendir-telemetry-stream-card",
        compact ? "expanded" : "",
        className,
      )}
      {...(compact ? handlers : {})}
      role={compact ? handlers.role : "article"}
      tabIndex={compact ? handlers.tabIndex : undefined}
      aria-expanded={compact ? handlers["aria-expanded"] : undefined}
      style={{
        ...cardStyle,
        cursor: compact ? "pointer" : undefined,
        transition: compact ? `all ${transitionDuration} ease-out` : undefined,
      }}
      aria-label={`${title} - ${isStreaming ? "Streaming" : "Paused"}`}
      aria-live="polite"
    >
      {/* Glass gradient overlay for bold/minimal themes */}
      {useGlassMode && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Unified Card Header - handles all theme variants */}
      <CardHeader
        icon="antenna-receive"
        title={title}
        subtitle={`${channelCount} channels • ${frequency} Hz`}
        status={isStreaming ? "normal" : "standby"}
        badge={isStreaming ? "Streaming" : "Paused"}
        badgeColor={
          isStreaming
            ? tokens.colors.status.normal
            : tokens.colors.status.standby
        }
      />

      {/* Metrics Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 12,
        }}
      >
        {metrics.map((metric) => (
          <div
            key={metric.id}
            style={metricBoxStyle}
            role="group"
            aria-label={`${metric.label}: ${metric.value}${metric.unit || ""}`}
          >
            <div
              style={{
                fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
                fontWeight: tokens.typography.fontWeight.medium, // 500 (AstroUXDS medium)
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: tokens.colors.text.tertiary,
                marginBottom: 4,
              }}
            >
              {metric.label}
            </div>
            <div
              style={{
                fontSize: tokens.typography.fontSize.lg,
                fontWeight: tokens.typography.fontWeight.bold, // 700 (AstroUXDS bold)
                fontFamily: tokens.typography.fontFamily.mono,
                fontVariantNumeric: "tabular-nums",
                color: getMetricColor(metric.status, tokens),
              }}
            >
              {typeof metric.value === "number"
                ? metric.format
                  ? metric.format(metric.value)
                  : metric.value.toLocaleString()
                : metric.value}
              {metric.unit && (
                <span
                  style={{
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: tokens.typography.fontWeight.normal, // 400 (AstroUXDS normal)
                    marginLeft: 2,
                  }}
                >
                  {metric.unit}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </article>
  );
});
